import { LABEL_PREFIX } from './constants';
import { labelNameForSlug } from './slug';

/**
 * The Gmail label a wedding's mail is filed under.
 *
 * By default this is `VendorOps/<slug>`, derived. A planner may instead name it
 * themselves at creation, because the label lands in *their* mailbox alongside
 * a filing system they already have — a planner who keeps everything under
 * `Clients/` wants this label there, not in a second top-level folder chosen by
 * us. The picked name is the whole label path, so nesting it anywhere is just
 * typing the path: `ensureLabel` creates each ancestor.
 *
 * Stored as NULL when the planner did not pick one, rather than as a copy of
 * the derived name. That is what keeps the pre-send rename rule working: a
 * wedding still following its slug moves its label when the couple's names
 * change before the first send, and one with a picked name never does.
 */

export interface WeddingLabelSource {
  slug: string;
  gmailLabelName?: string | null;
}

/** The label name to apply for this wedding — picked if there is one, derived otherwise. */
export function weddingLabelName(wedding: WeddingLabelSource, prefix = LABEL_PREFIX): string {
  const picked = wedding.gmailLabelName?.trim();
  return picked ? picked : labelNameForSlug(wedding.slug, prefix);
}

/** Gmail's cap on a label name, nesting path included. */
export const GMAIL_LABEL_MAX_LENGTH = 225;

/**
 * Gmail's own labels. A user label may not take one of these names, and Gmail
 * compares case-insensitively — creating "inbox" fails the same way "INBOX"
 * does. Checked per segment, since `Clients/Inbox` is refused too.
 */
const RESERVED_SEGMENTS = new Set([
  'inbox',
  'sent',
  'draft',
  'drafts',
  'spam',
  'trash',
  'unread',
  'starred',
  'important',
  'chat',
  'chats',
  'all mail',
]);

/**
 * Trims each segment and drops empty ones, so `  Clients / Smith ` and
 * `Clients//Smith` both settle to `Clients/Smith`.
 *
 * Gmail stores a label name verbatim, so ` Smith` and `Smith` would be two
 * different labels in the sidebar and the planner could not tell them apart.
 * Normalizing before storing is what stops that from ever being created.
 */
export function normalizeGmailLabelName(raw: string): string {
  return raw
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment !== '')
    .join('/');
}

/**
 * Why a name cannot be used, or null when it can. Returns a planner-facing
 * sentence: this is shown in the create dialog, not logged.
 *
 * Expects an already-normalized name.
 */
export function gmailLabelNameProblem(name: string): string | null {
  if (name === '') return 'Enter a label name';
  if (name.length > GMAIL_LABEL_MAX_LENGTH) {
    return `Gmail labels are at most ${GMAIL_LABEL_MAX_LENGTH} characters`;
  }
  // Escaped rather than literal: a control character pasted into the source
  // would be invisible to the next reader of this file.
  if (/[\u0000-\u001f\u007f]/.test(name)) {
    return 'Label names cannot contain control characters';
  }

  const reserved = name
    .split('/')
    .find((segment) => RESERVED_SEGMENTS.has(segment.toLowerCase()));
  if (reserved) return `"${reserved}" is one of Gmail's own labels — pick another name`;

  return null;
}
